import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react"

import "./MatchPairsAnswers2.scss"
import Button from "@ui/Button"
import i18nInstance from "@helper/i18nInstance"
import { useQuiz } from "@Quiz/context"
import _ from "lodash"
import onEnterOrSpace from "@helper/onEnterOrSpace"

// Visual label per left-column position (left options show their own, right options show
// their matched left option's) - "?" (see OptionBox) is the fallback for more than this
// many left options, or for an unmatched right option.
const COLUMN_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"]

// defined in ColorSchemes.tsx as --quiz__pairsMarker1..6 CSS variables on the quiz
const AMOUNT_MARKER_COLORS = 6
const markerColorForIndex = (index: number) => `var(--quiz__pairsMarker${(index % AMOUNT_MARKER_COLORS) + 1})`
const UNSET_MARKER_COLOR = "var(--quiz__pairsMarkerUnset)"

// elbow connector: horizontal from the left box, a rounded bend into vertical, straight down/up
// to the matched right box's height, a rounded bend back into horizontal
const CONNECTOR_CORNER_RADIUS = 4
// the horizontal stub leaving a left box sits slightly above its center, the one entering a
// right box slightly below its center, so the two never visually line up into one straight edge
const CONNECTOR_END_OFFSET = 4

const buildConnectorPath = (y1: number, y2: number, width: number, bendX: number): string => {
  if (y1 === y2) { return `M 0,${y1} H ${width}` }

  const dir = y2 > y1 ? 1 : -1
  const r = Math.min(CONNECTOR_CORNER_RADIUS, Math.abs(y2 - y1) / 2, bendX, width - bendX)

  return [
    `M 0,${y1}`,
    `H ${bendX - r}`,
    `Q ${bendX},${y1} ${bendX},${y1 + r * dir}`,
    `V ${y2 - r * dir}`,
    `Q ${bendX},${y2} ${bendX + r},${y2}`,
    `H ${width}`,
  ].join(" ")
}

// spreads each left option's bend evenly across the connectors width, strictly between its
// edges, so lines don't all bend at the same x (and overlap/cross messily)
const bendXForIndex = (index: number, totalLeftOptions: number, width: number) => width * (index + 1) / (totalLeftOptions + 1)

interface ConnectorLine {
  key: string
  path: string
  color: string
}

interface MatchPairsAnswers2Props {
  conf: Quiz.Api.MatchPairs.QuestionConfig
  tryAgain?: Quiz.Api.MatchPairs.TryAgainInfo
}

interface MatchPairsState {
  selectedMatches: Quiz.Api.MatchPairs.MatchForSubmission[]
  markEvalErrors: boolean
  canSubmitInfo: CanSubmitInfo
  selected?: MatchPairsSelection
}

type CanSubmitInfo =
  | { can: true }
  | { can: false, reason: "notAllMatched" | "duplicateMatching"}

const calcCanSubmitInfo = (config: Quiz.Api.MatchPairs.QuestionConfig, matches: Quiz.Api.MatchPairs.MatchForSubmission[]): CanSubmitInfo => {
  if (matches.length < config.options_left.length) {
    return { can: false, reason: "notAllMatched" }
  } else {
    const rightVals = _.map(matches, "right")
    const hasDuplicate = _.uniq(rightVals).length !== rightVals.length
    if (hasDuplicate) {
      return { can: false, reason: "duplicateMatching" }
    }
  }
  return { can: true }
}

const useMatchPairsState = (config: Quiz.Api.MatchPairs.QuestionConfig, tryAgain?: Quiz.Api.MatchPairs.TryAgainInfo) => {
  const [ state, setState ] = useState<MatchPairsState>({
    selectedMatches: [],
    markEvalErrors: false,
    canSubmitInfo: { can: false, reason: "notAllMatched" },
    selected: undefined,
  })
  const configRef = useRef(config)
  configRef.current = config
  const { busy, quiz } = useQuiz()

  const onMakeMatch = useCallback((match: Quiz.Api.MatchPairs.MatchForSubmission) => { // toggle val-selection, clear markEvalErrors
    setState((prev) => {
      const newMatches: MatchPairsState["selectedMatches"] = [
        ...prev.selectedMatches.filter((m) => m.left !== match.left && m.right !== match.right),
        match,
      ]
      return {
        selectedMatches: newMatches,
        markEvalErrors: false, // always clear on selection change
        canSubmitInfo: calcCanSubmitInfo(configRef.current, newMatches),
        selected: undefined,
      }
    })
  }, [])

  const setSelected = useCallback((selected: MatchPairsSelection | undefined) => {
    setState((prev) => ({ ...prev, selected, markEvalErrors: false })) // clear on selection change, same as onMakeMatch
  }, [])

  const selectedRef = useRef(state.selected)
  selectedRef.current = state.selected

  const onSelect = useCallback((selection: MatchPairsSelection) => {
    const selected = selectedRef.current

    if (!selected) {
      setSelected(selection)
      return
    }

    if ("left" in selected && "left" in selection) {
      setSelected(selected.left === selection.left ? undefined : selection)
    } else if ("right" in selected && "right" in selection) {
      setSelected(selected.right === selection.right ? undefined : selection)
    } else if ("left" in selected && "right" in selection) {
      onMakeMatch({ left: selected.left, right: selection.right })
    } else if ("right" in selected && "left" in selection) {
      onMakeMatch({ left: selection.left, right: selected.right })
    }
  }, [onMakeMatch, setSelected])

  const hasEval = !!quiz?.question?.eval

  useEffect(() => { // set markEvalErrors when eval but not (no longer) busy
    if (!busy && hasEval && tryAgain) {
      setState({
        selectedMatches: [], // sd wants matches reset (not restored) after a wrong try-again
        markEvalErrors: true,
        canSubmitInfo: calcCanSubmitInfo(configRef.current, []),
      })
    }
  }, [tryAgain, hasEval, busy])

  return { state, onMakeMatch, setSelected, onSelect }
}

// The columns (hardcoded to the revealed correct_matches - no onSelect/selected, this view
// isn't interactive) only show when the question was answered incorrectly and this quiz
// actually reveals correct_matches.
export const MatchPairs2ResultInfo: React.FC<{ question: Quiz.Api.MatchPairsQuestion}> = ({ question }) => {
  const result = question.eval?.result
  const correctMatches = result && !result.was_correct ? result.result_info?.correct_matches : undefined

  return <div className="neo__matchPairs2__resultInfo" data-testid="match-pairs2-result-info">
    {question.config && correctMatches &&
      <>
        <div className="neo__matchPairs2__resultInfo__correctList__title">{i18nInstance.t("quiz.question.match_pairs2.reveal_title")}</div>
        <div className="neo__matchPairs2__resultInfo__correctList__body">
          <MatchPairsColumns conf={question.config} selectedMatches={correctMatches} />
        </div>
      </>}
  </div>
}

// left and right options are rendered the same way - just plain boxes in their own column.
// boxRef is collected by MatchPairsColumns to later compute connector-line endpoints from.
interface OptionBoxProps {
  option: Quiz.Api.AnswerOptionBase
  side: "left" | "right"
  letter: string // "A"/"B"/... for the left option this one is (or is matched to), "?" as fallback
  markerColor: string // a var(--quiz__pairsMarkerN) or var(--quiz__pairsMarkerUnset) expression
  matchedOption?: Quiz.Api.AnswerOptionBase // the option on the *other* side this one is matched to, if any
  selected: boolean
  dimmed: boolean // another option on the same side is currently selected
  onClick?: () => void
  boxRef?: (el: HTMLDivElement | null) => void
  onImageLoad?: () => void // image loaded late and may have shifted box positions - recalc connector lines
}

const OptionBox: React.FC<OptionBoxProps> = ({ option, side, letter, markerColor, matchedOption, selected, dimmed, onClick, boxRef, onImageLoad }) => {
  const isOnlyText = !!option.text && !option.image
  const isOnlyImage = !!option.image && !option.text
  const isTextAndImage = !!option.text && !!option.image
  const hasOnlyImageCredit = isOnlyImage && !!option.image_credit
  const hasLongText = !!option.text && option.text.length > 28

  const imageAndTextHolderClassName = [
    "neo__matchPairs2__optionBox__imageAndTextHolder",
    isOnlyText ? "neo__matchPairs2__optionBox__imageAndTextHolder--onlyText" : "",
    isOnlyImage ? "neo__matchPairs2__optionBox__imageAndTextHolder--onlyImage" : "",
    isTextAndImage ? "neo__matchPairs2__optionBox__imageAndTextHolder--textAndImage" : "",
  ].join(" ")

  const imageClassName = [
    "neo__matchPairs2__optionBox__image__img",
    option.image_display_size === "large" ? "neo__matchPairs2__optionBox__image__img--large" : "",
  ].join(" ")

  const className = [
    "neo__matchPairs2__optionBox",
    `neo__matchPairs2__optionBox--${side}`,
    matchedOption ? "neo__matchPairs2__optionBox--matched" : "",
    selected ? "neo__matchPairs2__optionBox--selected" : "",
    dimmed ? "neo__matchPairs2__optionBox--dimmed" : "",
    isOnlyText ? "neo__matchPairs2__optionBox--onlyText" : "",
    isOnlyImage ? "neo__matchPairs2__optionBox--onlyImage" : "",
    isTextAndImage ? "neo__matchPairs2__optionBox--textAndImage" : "",
    hasOnlyImageCredit ? "neo__matchPairs2__optionBox--onlyImageWithCredit" : "",
    hasLongText ? "neo__matchPairs2__optionBox--longText" : "",
    onClick ? "neo__matchPairs2__optionBox--interactable" : "",
  ].join(" ")

  return <div
    ref={boxRef}
    className={className}
    data-testid={`match-pairs2-optionBox-${side}-${option.val}`}
    data-testmatch={matchedOption?.val}
    style={{ "--markerCol": markerColor } as React.CSSProperties}
    role={onClick ? "button" : undefined}
    aria-label={onClick && isOnlyImage ? String(option.val) : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? onEnterOrSpace(onClick) : undefined}>
    <span className="neo__matchPairs2__optionBox__letter">{letter}</span>
    <div className={imageAndTextHolderClassName}>
      {option.image &&
        <span className="neo__matchPairs2__optionBox__image">
          <img className={imageClassName} src={option.image["1x"]} onLoad={onImageLoad} draggable={false} />
          {option.image_credit && <div className="neo__matchPairs2__optionBox__image__imgCredit">{option.image_credit}</div>}
        </span>}
      {option.text &&
        <span className="neo__matchPairs2__optionBox__text" dangerouslySetInnerHTML={{__html: option.text}} />}
    </div>
  </div>
}

// The side+val a user has picked and is waiting to match with something on the other side.
export type MatchPairsSelection = { left: number } | { right: number }

interface MatchPairsColumnsProps {
  conf: Quiz.Api.MatchPairs.QuestionConfig
  selectedMatches: Quiz.Api.MatchPairs.MatchForSubmission[]
  // both absent in the (not yet interactive) result view; the interactive answers view
  // will fill them from a hook, once that exists
  onSelect?: (selection: MatchPairsSelection) => void
  selected?: MatchPairsSelection
}

// Shared between the interactive answers view and the result view (which hardcodes
// selectedMatches to the revealed correct_matches, and passes no onSelect/selected).
const MatchPairsColumns: React.FC<MatchPairsColumnsProps> = ({ conf, selectedMatches, onSelect, selected }) => {
  // collected to later compute connector-line endpoints from - key is "left-<val>"/"right-<val>"
  const boxRefs = useRef(new Map<string, HTMLDivElement>())
  const boxKey = (side: "left" | "right", val: number) => `${side}-${val}`
  const setBoxRef = (side: "left" | "right", val: number) => (el: HTMLDivElement | null) => {
    const key = boxKey(side, val)
    if (el) { boxRefs.current.set(key, el) } else { boxRefs.current.delete(key) }
  }

  // bumped whenever box positions may have shifted (container resize or a late image load),
  // to retrigger the connector-line layout effect below
  const [ recalcTick, triggerRecalc ] = useReducer((c: number) => c + 1, 0)

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const container = containerRef.current
    if (!container) { return }
    const observer = new ResizeObserver(triggerRecalc)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const connectorsRef = useRef<HTMLDivElement>(null)
  const [ connectorsSize, setConnectorsSize ] = useState({ width: 0, height: 0 })
  const [ connectorLines, setConnectorLines ] = useState<ConnectorLine[]>([])

  useLayoutEffect(() => {
    const connectorsEl = connectorsRef.current
    if (!connectorsEl) { return }

    const { width, height } = connectorsEl.getBoundingClientRect()
    setConnectorsSize({ width, height })

    const lines = _.compact(selectedMatches.map((match): ConnectorLine | undefined => {
      const leftEl = boxRefs.current.get(boxKey("left", match.left))
      const rightEl = boxRefs.current.get(boxKey("right", match.right))
      if (!leftEl || !rightEl) { return undefined }

      const connectorsRect = connectorsEl.getBoundingClientRect()
      const leftRect = leftEl.getBoundingClientRect()
      const rightRect = rightEl.getBoundingClientRect()
      const y1 = leftRect.top + leftRect.height / 2 - connectorsRect.top - CONNECTOR_END_OFFSET
      const y2 = rightRect.top + rightRect.height / 2 - connectorsRect.top + CONNECTOR_END_OFFSET

      const leftIndex = conf.options_left.findIndex((l) => l.val === match.left)
      const bendX = leftIndex === -1 ? width / 2 : bendXForIndex(leftIndex, conf.options_left.length, width)

      return {
        key: `${match.left}-${match.right}`,
        path: buildConnectorPath(y1, y2, width, bendX),
        color: leftIndex === -1 ? UNSET_MARKER_COLOR : markerColorForIndex(leftIndex),
      }
    }))
    setConnectorLines(lines)
  }, [conf, selectedMatches, recalcTick])

  const letterForLeftIndex = (index: number) => COLUMN_LETTERS[index] ?? "?"

  const matchedRightFor = (leftVal: number) => {
    const match = selectedMatches.find((m) => m.left === leftVal)
    return match && conf.options_right.find((r) => r.val === match.right)
  }
  // also returns the matched left option's own column index, to look up its letter
  const matchedLeftFor = (rightVal: number) => {
    const match = selectedMatches.find((m) => m.right === rightVal)
    if (!match) { return undefined }
    const index = conf.options_left.findIndex((l) => l.val === match.left)
    return index === -1 ? undefined : { option: conf.options_left[index], index }
  }

  return <div className="neo__matchPairs2__answers" ref={containerRef}>
    <div className="neo__matchPairs2__column">
      {conf.options_left.map((l, index) => {
        const matchedRight = matchedRightFor(l.val)
        const isSelected = !!selected && "left" in selected && selected.left === l.val
        const isDimmed = !!selected && "left" in selected && selected.left !== l.val
        return <OptionBox
          key={l.val}
          boxRef={setBoxRef("left", l.val)}
          onImageLoad={triggerRecalc}
          option={l}
          side="left"
          letter={letterForLeftIndex(index)}
          // matched or selected (even if not yet matched): own index's color, else unset
          markerColor={(matchedRight || isSelected) ? markerColorForIndex(index) : UNSET_MARKER_COLOR}
          matchedOption={matchedRight}
          selected={isSelected}
          dimmed={isDimmed}
          onClick={onSelect && (() => onSelect({ left: l.val }))}
        />
      })}
    </div>
    <div className="neo__matchPairs2__connectors" ref={connectorsRef}>
      {connectorsSize.width > 0 && connectorsSize.height > 0 &&
        <svg
          className={`neo__matchPairs2__connectorsSvg${selected ? " neo__matchPairs2__connectorsSvg--dimmed" : ""}`}
          width={connectorsSize.width}
          height={connectorsSize.height}>
          {connectorLines.map((line) => (
            <path key={line.key} d={line.path} style={{ stroke: line.color }} fill="none" strokeWidth={2} />
          ))}
        </svg>}
    </div>
    <div className="neo__matchPairs2__column">
      {conf.options_right.map((r) => {
        const matchedLeft = matchedLeftFor(r.val)
        const isSelected = !!selected && "right" in selected && selected.right === r.val
        const isDimmed = !!selected && "right" in selected && selected.right !== r.val
        return <OptionBox
          key={r.val}
          boxRef={setBoxRef("right", r.val)}
          onImageLoad={triggerRecalc}
          option={r}
          side="right"
          letter={matchedLeft ? letterForLeftIndex(matchedLeft.index) : "?"}
          // matched left's color if matched, else unset
          markerColor={matchedLeft ? markerColorForIndex(matchedLeft.index) : UNSET_MARKER_COLOR}
          matchedOption={matchedLeft?.option}
          selected={isSelected}
          dimmed={isDimmed}
          onClick={onSelect && (() => onSelect({ right: r.val }))}
        />
      })}
    </div>
  </div>
}

const MatchPairsAnswers2: React.FC<MatchPairsAnswers2Props> = ({ conf, tryAgain }) => {
  const { state, onSelect } = useMatchPairsState(conf, tryAgain)
  const { actions } = useQuiz()
  const selectedMatchesRef = useRef(state.selectedMatches)
  selectedMatchesRef.current = state.selectedMatches

  const answerQuestion = useCallback(() => {
    actions.answerQuestion({ type: "match_pairs", matches: selectedMatchesRef.current })
  }, [actions])

  return <div className="neo__matchPairs2__holder" data-testid="match-pairs2-answers">
    <div className="neo__matchPairs2__answerInfo">{i18nInstance.t("quiz.question.match_pairs2.title_what_todo")}</div>
    <MatchPairsColumns conf={conf} selectedMatches={state.selectedMatches} onSelect={onSelect} selected={state.selected} />
    <div className="neo__matchPairs2__submitButtonHolder">
      {/* right after a wrong try-again, the notAllMatched reason is hidden until the user interacts again */}
      {!state.canSubmitInfo.can && !(state.canSubmitInfo.reason === "notAllMatched" && state.markEvalErrors) &&
        <span className="neo__matchPairs2__cantSubmitReason">
          {state.canSubmitInfo.reason === "notAllMatched"
            ? i18nInstance.t("quiz.question.match_pairs2.cant_submit_because_notAllMatched")
            : i18nInstance.t("quiz.question.match_pairs.cant_submit_because_duplicateMatching")}
        </span>}
      <Button
        onClick={state.canSubmitInfo.can ? answerQuestion : undefined}>
        {i18nInstance.t("quiz.question.match_pairs.btn_send_answer")}
      </Button>
    </div>
  </div>
}

export default MatchPairsAnswers2
